import React, { useState } from 'react'
import {Errors, WithForm} from '@startlibs/form'
import {useEnsureRef} from '@startlibs/core'
import { buildValidation, intlBooleanRequired, intlConfirmEmails, intlEmailValidation, intlRequired, onlyIfEqual, onlyIfNotEqual, optional } from '../utils/validation'
import _ from 'lodash/fp'
import { callIfFunction, formatDate } from '@startlibs/utils'
import {LOCAL_PHYSICIAN, OTHER, PATIENT, REF_PHYSICIAN} from '../enums/ContactRelationType'
import { checkInsurance, INSURANCE_REQUIRED_KEYS, removeExtraInsuranceFields, STEP_CASE_INFO, STEP_CONTACT_INFO, STEP_INSURANCE, STEP_PATIENT_INFO, STEP_PHYSICIANS, TAB_CUSTOM_ID_PREFIX, TAB_TYPE_CUSTOM } from './forms/utils'
import {AcceptTermsCheckbox, requiredTerms} from './components/AcceptTermsCheckbox'
import {IfLocale, useIsLocale} from '../hocs/IfLocale'
import { lazyProviderInfo } from '../components/WithProvider'
import { CountryRegionData } from 'react-country-region-selector'
import { PatientDetailsNew } from './newforms/PatientDetailsNew'
import { ContactDetailsNew } from './newforms/ContactDetailsNew'
import { ReferringPhysicianNew } from './newforms/ReferringPhysicianNew'
import { InsuranceDetailsNew } from './newforms/InsuranceDetailsNew'
import { PrimaryConcernNew } from './newforms/PrimaryConcernNew'
import { CaseReconciliationNew } from './newforms/CaseReconciliationNew'
import { Button, Icon } from '@startlibs/components'
import { CaseSettingsNew } from './newforms/CaseSettingsNew'
import { lazyPublicCategories, lazyUserCategories } from '../admin/experts/hooks/useEditCategories'
import styled from 'styled-components'
import { CustomFields } from './newforms/CustomFields'
import { DATE, getTranslatedDescription, getTranslatedTitles } from '../admin/controlPanel/intakeUtils'
import { FormattedMessage, useIntl } from 'react-intl'

const StepButtonsContainer = styled.div`
  justify-content: flex-end;
  padding-top: 1.5rem;
  margin-top: auto;
  display: flex;
`

const StepButton = styled(Button)`
  box-shadow: none;
  padding: 9px 1.25rem;
  font-weight: 600;
  ${Icon} {
    font-size: 12px !important;
  }
`

const NextStepButtonIcon = styled(Icon)`
  vertical-align: -1px !important;
  margin-left: 0.5rem;
`
const PreviousStepButtonIcon = styled(Icon)`
  vertical-align: -1px !important;
  margin-right: 0.5rem;
`

export const getFormattedStates = (country) => {
  var adjustedCountry = country == 'USA' ? 'United States' : country
  const selectedCountryData = CountryRegionData.find(data => {
    return data[0] === adjustedCountry || data[1] === adjustedCountry
  })
  const stateOptions = selectedCountryData 
    ? selectedCountryData[2].split("|").map(state => state.split("~")[0])
    : []
    stateOptions.unshift('')
  return stateOptions
}


const alreadyHasEmail = (path, caseRequest, props) => caseRequest && _.get(path, caseRequest) === _.get(path, props)


export const PatientAndContactDetailsNew = ({
                                           hideErrors, onChange,
                                           action, isPatient, isNurse, isRefPhysician, isPatientCreating, readOnly, onSuccess, onFailure, children, formRef, caseRequest, isAdmin, acceptedJurisdictions = null,
                                           steps, selectedStepNumber, setSelectedStepNumber,
                                           categories, markedAsPriority,
                                           values = {
    contactDetails: {contactRelationType: isRefPhysician ? REF_PHYSICIAN : PATIENT},
    patientInfo: {insurances:[]},
    referringPhysician: isPatientCreating ? {acceptContact: false} : {},
    caseInfo: {hasNewDiagnosisChangedTreatmentOrRecommendation: null},
    // caseInfo: {},
    categories:[],
  }
                                         }) => {


  const ensuredFormRef = useEnsureRef(formRef)
  const providerInfo = lazyProviderInfo.read()

  const isMultiLanguage = providerInfo?.preferredLanguage?.options && providerInfo?.preferredLanguage?.options?.length > 1
  const defaultLanguage = providerInfo?.preferredLanguage?.defaultLanguage
  const intl = useIntl()

  const [contactPhoneNumberValid, setContactPhoneNumberValid] = useState(true)
  const [contactFaxNumberValid, setContactFaxNumberValid] = useState(true)
  const [physicianPhoneNumber1Valid, setPhysicianPhoneNumber1Valid] = useState(true)
  const [physicianFaxNumber1Valid, setPhysicianFaxNumber1Valid] = useState(true)
  const [physicianPhoneNumber2Valid, setPhysicianPhoneNumber2Valid] = useState(true)
  const [physicianFaxNumber2Valid, setPhysicianFaxNumber2Valid] = useState(true)

  const NavigationButtons = () => {
    // Find the next and previous step numbers based on availability
    const stepNumbers = steps.map((step) => step.order);
    const currentIndex = stepNumbers.indexOf(selectedStepNumber);
  
    const getNextStep = () => stepNumbers[currentIndex + 1] ?? null;
    const getPreviousStep = () => stepNumbers[currentIndex - 1] ?? null;
  
    return (
      <StepButtonsContainer>
        {(steps && steps.length > 1 && selectedStepNumber >= 1) && 
          selectedStepNumber === stepNumbers[stepNumbers.length - 1] ?
          <StepButton outline rounded onClick={() => setSelectedStepNumber(getPreviousStep())}>
            <PreviousStepButtonIcon icon="arrow-left"/><FormattedMessage 
              defaultMessage="Previous step"
              description="Previous step intake form"
            />
          </StepButton>
          :
          selectedStepNumber >= 1 && selectedStepNumber !== stepNumbers[0] && <StepButton outline onlyIcon icon="arrow-left" rounded onClick={() => setSelectedStepNumber(getPreviousStep())}/>
        }
        {steps && steps.length > 1 && currentIndex < stepNumbers.length - 1 && (
          <StepButton outline rounded onClick={() => setSelectedStepNumber(getNextStep())}>
            <FormattedMessage
              defaultMessage="Next step"
              description="Next step intake form"
            />
          <NextStepButtonIcon icon="arrow-right"/></StepButton>
        )}
      </StepButtonsContainer>
    );
  };

  // Country and Regions data using react-country-region-selector
  // Documentation: https://country-regions.github.io/react-country-region-selector/
  // insert an empty option at the beginning
  const countryList = CountryRegionData.map(country => country[0])
  // Find the index of 'United States'
  const usIndex = countryList.findIndex(country => country === 'United States');
  if (usIndex !== -1) {
    // Remove 'United States' from its current position
    const usItem = countryList.splice(usIndex, 1)[0];
    // Add 'United States' to the start of the array
    countryList.unshift(usItem);
  }
  countryList.unshift('')
  
  const patientCountryAndStateEmpty = caseRequest?.patientInfo?.address?.country == '' && caseRequest?.patientInfo?.address?.state == ''
  const patientCountryAndStateNotExists= caseRequest?.patientInfo?.address == null
  const patientCountryAndStateNull= caseRequest?.patientInfo?.address?.country == null && caseRequest?.patientInfo?.address?.state == null
  const patientCountry = caseRequest?.patientInfo?.address?.country === 'USA' ? 'US' : caseRequest?.patientInfo?.address?.country
  const patientState = caseRequest?.patientInfo?.address?.state
  const patientCountryInList = patientCountry && CountryRegionData.find(country => country[0] === patientCountry || country[1] === patientCountry)
  const patientStateInList = patientState && patientCountryInList && patientCountryInList[2].split("|")
    .find(state => state.split("~")[0] === patientState || state.split("~")[1] === patientState)
  const fullCountryName = patientCountryInList && patientCountryInList[0]
  const fullStateName = patientStateInList && patientStateInList.split("~")[0]
  const showSelects = (!!fullCountryName && !!fullStateName) || (patientCountryAndStateEmpty) || (patientCountryAndStateNotExists) || (patientCountryAndStateNull)

  const contactCountryAndStateEmpty = caseRequest?.contactDetails?.address?.country == '' && caseRequest?.contactDetails?.address?.state == ''
  const contactCountryAndStateNotExists= caseRequest?.contactDetails?.address == null 
  const contactCountryAndStateNull= caseRequest?.contactDetails?.address?.country == null && caseRequest?.contactDetails?.address?.state == null
  const contactCountry = caseRequest?.contactDetails?.address?.country === 'USA' ? 'US' : caseRequest?.contactDetails?.address?.country
  const contactState = caseRequest?.contactDetails?.address?.state
  const contactCountryInList = contactCountry && CountryRegionData.find(country => country[0] === contactCountry || country[1] === contactCountry)
  const contactStateInList = contactState && contactCountryInList && contactCountryInList[2].split("|")
    .find(state => state.split("~")[0] === contactState || state.split("~")[1] === contactState)
  const fullContactCountryName = contactCountryInList && contactCountryInList[0]
  const fullContactStateName = contactStateInList && contactStateInList.split("~")[0]
  const showContactSelects = (!!fullContactCountryName && !!fullContactStateName) || (contactCountryAndStateNotExists) || (contactCountryAndStateEmpty) || (contactCountryAndStateNull)
  
  
  const phyCountryAndStateEmpty = caseRequest?.referringPhysician?.address?.country == '' && caseRequest?.referringPhysician?.address?.state == ''
  const phyCountryAndStateNotExists= caseRequest?.referringPhysician?.address == null
  const phyCountryAndStateNull= caseRequest?.referringPhysician?.address?.country == null && caseRequest?.referringPhysician?.address?.state == null
  const phyCountry = caseRequest?.referringPhysician?.address?.country === 'USA' ? 'US' : caseRequest?.referringPhysician?.address?.country
  const phyState = caseRequest?.referringPhysician?.address?.state
  const phyCountryInList = phyCountry && CountryRegionData.find(country => country[0] === phyCountry || country[1] === phyCountry)
  const phyStateInList = phyState && phyCountryInList && phyCountryInList[2].split("|")
    .find(state => state.split("~")[0] === phyState || state.split("~")[1] === phyState)
  const fullPhyCountryName = phyCountryInList && phyCountryInList[0] 
  const fullPhyStateName = phyStateInList && phyStateInList.split("~")[0]
  const showPhySelects = (!!fullPhyCountryName && !!fullPhyStateName) || (phyCountryAndStateEmpty) || (phyCountryAndStateNotExists) || (phyCountryAndStateNull)
  
  const updatedValues = _.flow(
    fullCountryName ? _.set('patientInfo.address.country', fullCountryName) : _.identity,
    fullStateName ? _.set('patientInfo.address.state', fullStateName) : _.identity,
    fullContactCountryName ? _.set('contactDetails.address.country', fullContactCountryName) : _.identity,
    fullContactStateName ? _.set('contactDetails.address.state', fullContactStateName) : _.identity,
    fullPhyCountryName ? _.set('referringPhysician.address.country', fullPhyCountryName) : _.identity,
    fullPhyStateName ? _.set('referringPhysician.address.state', fullPhyStateName) : _.identity,
    _.update('patientInfo.insurances',(insurances) => insurances?.length > 0 ? insurances : []),
    _.set('referringPhysicians', [{ role: REF_PHYSICIAN }]),
    providerInfo?.preferredLanguage?.options?.length > 1 && providerInfo?.preferredLanguage?.defaultLanguage ? _.set('caseInfo.preferredLanguage', providerInfo?.preferredLanguage?.defaultLanguage) : _.identity,
            
  )
  const transform = _.flow(
    (values) => values.contactDetails.contactRelationType === REF_PHYSICIAN ? _.set('referringPhysician', {}, values) : values,
    (values) => values.contactDetails.contactRelationType === LOCAL_PHYSICIAN ? _.set('localPhysician', {}, values) : values,
    _.update('patientInfo.dob', (date) => formatDate(new Date(date), "MM-dd-yyyy")),
    _.update('patientInfo.insurances', (insurances) => insurances?.filter((insurance,i) => !checkInsurance(insurance,i,true).length)),
    _.update('patientInfo.insurances', (insurances) => insurances.map(removeExtraInsuranceFields)),
    (values) => values?.patientInfo?.alternateAddress === "" ? _.unset('patientInfo.alternateAddress', values) : values,
    (values) => values.caseInfo.hasBeenTreatedOrSurgeryBefore === false 
      ?  _.update('caseInfo.treatedOrSurgeryBeforeDescription', () => '',values)
      : values,
    (values) => values.referringPhysicians?.length > 0 && values.referringPhysicians[0].role === REF_PHYSICIAN
      ? _.set('referringPhysician', values.referringPhysicians[0], values)
      : _.set('localPhysician', values.referringPhysicians[0], values),
    (values) => values.referringPhysicians?.length > 1 && values.referringPhysicians[1].role === REF_PHYSICIAN
      ? _.set('referringPhysician', values.referringPhysicians[1], values)
      : values.referringPhysicians?.length > 1 ? _.set('localPhysician', values.referringPhysicians[1], values) : values,
    (values) => {
      // get all the keys that start with "tab-"
      const tabKeys = Object.keys(values).filter((key) => key.startsWith(TAB_CUSTOM_ID_PREFIX));
  
      // Create a new object with the filtered keys
      const customFields = tabKeys.reduce((acc, key) => {
          acc[key] = values[key];
          return acc;
      }, {});

      const defaultSteps = steps.filter(step => step.type !== TAB_TYPE_CUSTOM && step.order < 98);
      
      // Update steps where type is CUSTOM
      const updatedCustomSteps = steps.filter(step => step.type === TAB_TYPE_CUSTOM).map(step => {
          
          // Get the corresponding tab data from customFields
          const tabData = customFields[step.id] || {};

          const updateValue = (id, type, value) => {
            const newValue = tabData[id] ?? value;

            if (type === DATE) {
              if (!newValue) return newValue; // preserve null/undefined/false
              return formatDate(new Date(newValue), "MM-dd-yyyy");
            }

            return newValue;
          };
          

          // Map through the lines and update the value if the ID exists in tabData
          const updatedLines = step.lines.map(line => ({
              left: {
                  ...line.left,
                  // value: tabData[line.left.id] ?? line.left.value
                  value: updateValue(line.left.id, line.left.type, line.left.value)
              },
              right: line.right
                  // ? { ...line.right, value: tabData[line.right.id] ?? line.right.value }
                  ? { ...line.right, value: updateValue(line.right.id, line.right.type, line.right.value) }
                  : null
          }));

          return { ...step, lines: updatedLines };
      });

      const intakeForm = {
        tabs: [
          ...defaultSteps,
          ...updatedCustomSteps
        ]
      }
  
      // Define customFields on the object and returns it
      return _.set('intakeForm', intakeForm, values)
    }
  )

  const isLegal = useIsLocale({contains:'LEGAL'})

  const preValidation = buildValidation({
    'categories': (categories) => categories && categories.find(_.isString) && "Please select a valid category from the list." ,
    'patientInfo.firstName': [intlRequired(intl)],
    'patientInfo.lastName': [intlRequired(intl)],
    'patientInfo.dob': [intlRequired(intl), (d) => d === -1 && "Invalid date"],
    'patientInfo.gender': [intlRequired(intl)],
    'patientInfo.address.country': [intlRequired(intl)],
    'patientInfo.address.state': [intlRequired(intl)],
    'patientInfo.address.city': [intlRequired(intl)],
    'patientInfo.address.address': [intlRequired(intl)],
    'patientInfo.address.zipCode': [intlRequired(intl)],
    'contactDetails.contactRelationType': [intlRequired(intl)],
    'contactDetails.fullName.first': [onlyIfNotEqual('contactDetails.contactRelationType', PATIENT, intlRequired(intl))],
    'contactDetails.fullName.last': [onlyIfNotEqual('contactDetails.contactRelationType', PATIENT, intlRequired(intl))],
    'contactDetails.otherRelationType': [onlyIfEqual('contactDetails.contactRelationType', OTHER, intlRequired(intl))],
    'contactDetails.email': [intlRequired(intl), intlEmailValidation(intl)],
    //  intlEmailValidation(intl), intlConfirmEmails(intl)('confirmEmail')
    'contactDetails.confirmEmail': [(confirm, k, props) => !alreadyHasEmail('contactDetails.email', caseRequest, props) && intlConfirmEmails(intl)('contactDetails.email')(confirm, k, props)],
    // 'contactDetails.phoneNumber': [required],
    // phone number is required and must only contain numbers and symbols but no letter
    // 'contactDetails.phoneNumber': [(v) => contactPhoneNumberValid ? undefined : "Please enter a valid phone number"],
    'contactDetails.phoneNumber': [intlRequired(intl), (_v) => contactPhoneNumberValid ? undefined : "Please enter a valid phone number"],
    'contactDetails.faxNumber': [
      onlyIfEqual('contactDetails.contactRelationType', REF_PHYSICIAN, (v) => (
        (v && v.match(/^[0-9\-+()\s]+$/) && contactFaxNumberValid) || v === "" || !v) ? undefined : "Please enter a valid fax number"),
      onlyIfEqual('contactDetails.contactRelationType', LOCAL_PHYSICIAN, (v) => (
        (v && v.match(/^[0-9\-+()\s]+$/) && contactFaxNumberValid) || v === "" || !v) ? undefined : "Please enter a valid fax number")
    ],
    // 'contactDetails.phoneNumber': [required, (v) => v && v.match(/^[0-9\-\+\(\)\s]+$/) ? undefined : "Please enter a valid phone number"],
    'contactDetails.prefCommunicationMode': [intlRequired(intl)],
    // 'contactDetails.prefContactTime': [required],
    // 'referringPhysician.name': [(v,k,props) => props.contactDetails.relationType !== REF_PHYSICIAN && required(v)],
    // 'referringPhysician.email': [onlyIfNotEqual('contactDetails.contactRelationType', REF_PHYSICIAN, optional(emailValidation))],
    // 'referringPhysician.confirmEmail': [onlyIfNotEqual('contactDetails.contactRelationType', REF_PHYSICIAN, (v, k, props) => !alreadyHasEmail('referringPhysician.email', caseRequest, props) && confirmEmails('referringPhysician.email')(v, k, props))],
    // 'localPhysician.email': [onlyIfNotEqual('contactDetails.contactRelationType', LOCAL_PHYSICIAN, optional(emailValidation))],
    // 'localPhysician.confirmEmail': [onlyIfNotEqual('contactDetails.contactRelationType', LOCAL_PHYSICIAN, (v, k, props) => !alreadyHasEmail('localPhysician.email', caseRequest, props) && confirmEmails('localPhysician.email')(v, k, props))],

    'referringPhysicians[0].email': [optional(intlEmailValidation(intl))],
    'referringPhysicians[0].confirmEmail': [(v, k, props) => !alreadyHasEmail('referringPhysicians[0].email', caseRequest, props) && intlConfirmEmails(intl)('referringPhysicians[0].email')(v, k, props)],
    'referringPhysicians[1].email': [optional(intlEmailValidation(intl))],
    'referringPhysicians[1].confirmEmail': [(v, k, props) => !alreadyHasEmail('referringPhysicians[1].email', caseRequest, props) && intlConfirmEmails(intl)('referringPhysicians[1].email')(v, k, props)],
    // 'referringPhysicians[0].phoneNumber': [(v) => !v || v === "" || physicianPhoneNumber1Valid ? undefined : "Please enter a valid phone number"],
    'referringPhysicians[0].phoneNumber': [(v) => !v || v === "" || physicianPhoneNumber1Valid ? undefined : intl.formatMessage({defaultMessage: "Please enter a valid phone number", description: "Validation error message for invalid phone number"})],
    // 'referringPhysicians[0].faxNumber': [(v) => !v || v === "" || physicianFaxNumber1Valid ? undefined : "Please enter a valid fax number"],
    'referringPhysicians[0].faxNumber': [(v) => !v || v === "" || physicianFaxNumber1Valid ? undefined : intl.formatMessage({defaultMessage: "Please enter a valid fax number", description: "Validation error message for invalid fax number"})],
    // 'referringPhysicians[1].phoneNumber': [(v) => !v || v === "" || physicianPhoneNumber2Valid ? undefined : "Please enter a valid phone number"],
    'referringPhysicians[1].phoneNumber': [(v) => !v || v === "" || physicianPhoneNumber2Valid ? undefined : intl.formatMessage({defaultMessage: "Please enter a valid phone number", description: "Validation error message for invalid phone number"})],
    // 'referringPhysicians[1].faxNumber': [(v) => !v || v === "" || physicianFaxNumber2Valid ? undefined : "Please enter a valid fax number"],
    'referringPhysicians[1].faxNumber': [(v) => !v || v === "" || physicianFaxNumber2Valid ? undefined : intl.formatMessage({defaultMessage: "Please enter a valid fax number", description: "Validation error message for invalid fax number"})],

    'caseInfo.clinicalSummary': [intlRequired(intl)],
    'caseInfo.intakeDiagnosis': isLegal ? [] : values?.caseInfo?.intakeDiagnosis !== null ? [intlRequired(intl)] : [],
    'caseInfo.hasBeenTreatedOrSurgeryBefore': isLegal ? [] : [intlBooleanRequired(intl)],
    'caseInfo.ehrInfo.mrn': (v) => v?.length > 255 && intl.formatMessage({
      defaultMessage: 'Medical Record Number must be 255 characters or less.',
      description: 'Validation error message for MRN max length'
    }),
    'caseInfo.ehrInfo.orderNumber': (v) => v?.length > 255 && intl.formatMessage({
      defaultMessage: 'Order Number must be 255 characters or less.',
      description: 'Validation error message for order number max length'
    }),
    'acceptedTerms': isPatient && requiredTerms(caseRequest),

    // This key is duplicated: the entry below overrides the required check; merge them if it is wanted.
    "referringPhysicians[0].role" : [intlRequired(intl)],
    // eslint-disable-next-line no-dupe-keys
    "referringPhysicians[0].role" : (v, k, props) => v && props.referringPhysicians[0].role && props.referringPhysicians.length > 1 
      // && props.referringPhysicians[1].role === v && "You can have only one physician of each type",
      && props.referringPhysicians[1].role === v && intl.formatMessage({defaultMessage: "You can have only one physician of each type", description: "Validation error message for only one physician of each type"}),
    "referringPhysicians[1].role" : (v, k, props) => v && props.referringPhysicians[1].role && props.referringPhysicians.length > 1 
      && (props.contactDetails.contactRelationType === REF_PHYSICIAN || props.contactDetails.contactRelationType === LOCAL_PHYSICIAN) 
      // && "You can have only one physician with a different type from your contact",
      && props.referringPhysicians[1].role === v && intl.formatMessage({defaultMessage: "You can have only one physician of each type", description: "Validation error message for only one physician of each type"}),
    ...Object.fromEntries(
      steps
        .filter(tab => tab.type === TAB_TYPE_CUSTOM) // Only process custom tabs
        .flatMap(tab =>
          // For each tab, process each line
          tab.lines.flatMap(line => {
            const rules = [];
            // Check left field
            if (line.left && line.left.required) {
              // rules.push([`customFields.${tab.id}.${line.left.name}.value`, [required]]);
              rules.push([`${tab.id}.${line.left.id}`, [intlRequired(intl)]]);
            }
            // Check right field
            if (line.right && line.right.required) {
              rules.push([`${tab.id}.${line.right.id}`, [intlRequired(intl)]]);
            }

            if( line.left && line.left.type === DATE) {
              rules.push([`${tab.id}.${line.left.id}`, [(d) => d === -1 && "Invalid date", line.left.required ? intlRequired(intl) : undefined]]);
            }
            if( line.right && line.right.type === DATE) {
              rules.push([`${tab.id}.${line.right.id}`, [(d) => d === -1 && "Invalid date", line.right.required ? intlRequired(intl) : undefined]]);
            }
            return rules;
          })
        )
    )
  }, ({patientInfo: {insurances}}) => {
    
    if (insurances && insurances.length) {
      const errorsListList = insurances.map((insurance,i) => checkInsurance(insurance,i))
      const hasOneSuccess = errorsListList.find((list, i) => i === 0 && !list.length)
      return {
        ..._.fromPairs(_.flatten(errorsListList.filter(list => hasOneSuccess ? list.length < INSURANCE_REQUIRED_KEYS.length : true))),
      }
    }
    
  })
  
  return <WithForm
    alwaysSave
    onChange={onChange}
    ref={ensuredFormRef}
    transform={transform}
    action={action}
    onSuccess={onSuccess}
    onFailure={onFailure}
    preValidation={preValidation}
    // values={fixMedicareInsideInsurances(values)}
    values={updatedValues(values)}
  >{form => <>
    {steps.find(s => s.order === selectedStepNumber)?.id === STEP_PATIENT_INFO && <PatientDetailsNew 
      title={getTranslatedTitles(STEP_PATIENT_INFO, intl)}
      subTitle={getTranslatedDescription(STEP_PATIENT_INFO, intl)}
      scrollTo={true} 
      isAdmin={isAdmin} 
      readOnly={readOnly} 
      form={form} 
      countryList={countryList} 
      showSelects={showSelects} 
      acceptedJurisdictions={isAdmin ? acceptedJurisdictions : null}
      isPatientCreating={isPatientCreating}
      NavigationButtons={NavigationButtons}
    />}
    {steps.find(s => s.order === selectedStepNumber)?.id === STEP_CONTACT_INFO && <ContactDetailsNew 
      title={getTranslatedTitles(STEP_CONTACT_INFO, intl)}
      subTitle={getTranslatedDescription(STEP_CONTACT_INFO, intl)}
      scrollTo={true} 
      readOnly={readOnly} 
      form={form} 
      countryList={countryList} 
      showSelects={showContactSelects} 
      isPatientCreating={isPatientCreating} 
      caseRequest={caseRequest} 
      contactPhoneNumberValid={contactPhoneNumberValid} 
      setContactPhoneNumberValid={setContactPhoneNumberValid}
      contactFaxNumberValid={contactFaxNumberValid}
      setContactFaxNumberValid={setContactFaxNumberValid}
      NavigationButtons={NavigationButtons}
    />}
    <IfLocale not contains='LEGAL'>
      {steps.find(s => s.order === selectedStepNumber)?.id === STEP_PHYSICIANS && <ReferringPhysicianNew 
        title={getTranslatedTitles(STEP_PHYSICIANS, intl)}
        subTitle={getTranslatedDescription(STEP_PHYSICIANS, intl)}
        scrollTo={true}
        readOnly={readOnly} 
        isAdmin={isAdmin} 
        form={form} 
        countryList={countryList} 
        showSelects={showPhySelects}
        physicianPhoneNumber1Valid={physicianPhoneNumber1Valid}
        setPhysicianPhoneNumber1Valid={setPhysicianPhoneNumber1Valid}
        physicianFaxNumber1Valid={physicianFaxNumber1Valid}
        setPhysicianFaxNumber1Valid={setPhysicianFaxNumber1Valid}
        physicianPhoneNumber2Valid={physicianPhoneNumber2Valid}
        setPhysicianPhoneNumber2Valid={setPhysicianPhoneNumber2Valid}
        physicianFaxNumber2Valid={physicianFaxNumber2Valid}
        setPhysicianFaxNumber2Valid={setPhysicianFaxNumber2Valid} 
        NavigationButtons={NavigationButtons}/>}
      {steps.find(s => s.order === selectedStepNumber)?.id === STEP_INSURANCE && <InsuranceDetailsNew 
        title={getTranslatedTitles(STEP_INSURANCE, intl)}
        subTitle={getTranslatedDescription(STEP_INSURANCE, intl)}
        scrollTo={true}
        readOnly={readOnly} 
        form={form} 
        NavigationButtons={NavigationButtons}/>
      }
    </IfLocale>
    {steps.find(s => s.order === selectedStepNumber)?.id === STEP_CASE_INFO && <PrimaryConcernNew 
      title={getTranslatedTitles(STEP_CASE_INFO, intl)}
      subTitle={getTranslatedDescription(STEP_CASE_INFO, intl)}
      readOnly={readOnly} 
      scrollTo={true} 
      NavigationButtons={NavigationButtons}/>
    }
    {steps.find(s => s.order === selectedStepNumber)?.type === TAB_TYPE_CUSTOM && <CustomFields 
      // title={steps.find(s => s.order === selectedStepNumber)?.name || (isMultiLanguage && steps.find(s => s.order === selectedStepNumber)?.translations && steps.find(s => s.order === selectedStepNumber)?.translations.find(t => t.language === intl.locale)?.name) }
      title={
        steps.find(s => s.order === selectedStepNumber)?.name ||
        (
          isMultiLanguage &&
          (() => {
            const translation = steps.find(s => s.order === selectedStepNumber)?.translations?.find(t => t.language === intl.locale);
            return translation?.name === "" ? "" : translation?.name;
          })()
        )
      }
      id={steps.find(s => s.order === selectedStepNumber)?.id} 
      // description={steps.find(s => s.order === selectedStepNumber)?.description || (isMultiLanguage && steps.find(s => s.order === selectedStepNumber)?.translations && steps.find(s => s.order === selectedStepNumber)?.translations.find(t => t.language === intl.locale)?.description) } 
      description={
        steps.find(s => s.order === selectedStepNumber)?.description ||
        (
          isMultiLanguage &&
          (() => {
            const translation = steps.find(s => s.order === selectedStepNumber)?.translations?.find(t => t.language === intl.locale);
            return translation?.description === "" ? "" : translation?.description;
          })()
        )
      }
      readOnly={readOnly} 
      scrollTo={true} 
      NavigationButtons={NavigationButtons}
      lines={steps.find(s => s.order === selectedStepNumber)?.lines}
      isMultiLanguage={isMultiLanguage}
      defaultLanguage={defaultLanguage}
      intl={intl}
      translations={steps.find(s => s.order === selectedStepNumber)?.translations}
    />}
    <IfLocale not contains='LEGAL'>
      {providerInfo?.caseReconciliationEnabled === true && isAdmin && caseRequest === undefined && 
        selectedStepNumber === 98 && <CaseReconciliationNew 
          title={steps.find(s => s.number === 98)?.pageTitle} 
          subTitle={steps.find(s => s.number === 98)?.pageSubTitle} 
          scrollTo={true}
          NavigationButtons={NavigationButtons}
        />
      }
    </IfLocale>
    {selectedStepNumber === 99 &&
      <CaseSettingsNew 
        lazyCategories={isNurse ? lazyPublicCategories : lazyUserCategories}
        title={steps.find(s => s.order === selectedStepNumber)?.pageTitle} 
        subTitle={steps.find(s => s.order === selectedStepNumber)?.pageSubTitle}
        scrollTo={true}
        NavigationButtons={NavigationButtons}
        form={form}
        caseRequest={{categories:categories?.isOpen,markedAsPriority:markedAsPriority.isOpen}}
                            setCaseRequest={(update) => form.setValues(update)}
        
      />
    }
    
    {isPatient && !readOnly && <AcceptTermsCheckbox caseRequest={caseRequest}/>}

    {!hideErrors && <Errors/>}
    {callIfFunction(children, form)}
  </>}</WithForm>
}
